export type Rgb = { r: number; g: number; b: number };
export type Hsl = { h: number; s: number; l: number };

const HEX_PATTERN = /^#([0-9a-f]{6})$/i;

export function isHexColor(value: string): boolean {
    return HEX_PATTERN.test(value.trim());
}

export function hexToRgb(hex: string): Rgb {
    const match = HEX_PATTERN.exec(hex.trim());
    if (!match) throw new Error(`Cor inválida: ${hex}`);
    const value = parseInt(match[1], 16);
    return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

export function rgbToHex({ r, g, b }: Rgb): string {
    const channel = (value: number) => Math.round(Math.min(255, Math.max(0, value))).toString(16).padStart(2, "0");
    return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase();
}

function linearize(channel: number): number {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string): number {
    const { r, g, b } = hexToRgb(hex);
    return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/** Razão de contraste WCAG 2.x entre duas cores, de 1 a 21. */
export function contrastRatio(foreground: string, background: string): number {
    const [light, dark] = [relativeLuminance(foreground), relativeLuminance(background)].sort((a, b) => b - a);
    return (light + 0.05) / (dark + 0.05);
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
    const [rn, gn, bn] = [r / 255, g / 255, b / 255];
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    if (max === min) return { h: 0, s: 0, l };

    const delta = max - min;
    const s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    const h = max === rn ? (gn - bn) / delta + (gn < bn ? 6 : 0) : max === gn ? (bn - rn) / delta + 2 : (rn - gn) / delta + 4;
    return { h: h * 60, s, l };
}

function hslToRgb({ h, s, l }: Hsl): Rgb {
    const chroma = (1 - Math.abs(2 * l - 1)) * s;
    const segment = (h % 360) / 60;
    const x = chroma * (1 - Math.abs((segment % 2) - 1));
    const [r1, g1, b1] =
        segment < 1 ? [chroma, x, 0] : segment < 2 ? [x, chroma, 0] : segment < 3 ? [0, chroma, x]
            : segment < 4 ? [0, x, chroma] : segment < 5 ? [x, 0, chroma] : [chroma, 0, x];
    const m = l - chroma / 2;
    return { r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 };
}

export function withLightness(hex: string, lightness: number): string {
    const hsl = rgbToHsl(hexToRgb(hex));
    return rgbToHex(hslToRgb({ ...hsl, l: Math.min(1, Math.max(0, lightness)) }));
}

export function withHsl(hsl: Hsl): string {
    return rgbToHex(hslToRgb({ h: hsl.h, s: Math.min(1, Math.max(0, hsl.s)), l: Math.min(1, Math.max(0, hsl.l)) }));
}
